import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { leistungen } from "@/data/leistungen";
import { T } from "@/components/T";
import { useLanguage } from "@/contexts/LanguageContext";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  });
}

export default function Leistungen() {
  useReveal();
  const { lang } = useLanguage();

  return (
    <div className="pt-20 md:pt-24">
      <section className="section-gap bg-hb-off-white border-b border-hb-border">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-6 reveal"><T de="Leistungen" it="Servizi" /></span>
          <h1 className="section-title max-w-2xl mb-6 reveal reveal-delay-1">
            <T de="Holzbau aus einer Hand." it="Costruzioni in legno da un unico fornitore." />
          </h1>
          <p className="section-subtitle max-w-xl reveal reveal-delay-2">
            <T de="Von der ersten Idee bis zur Übergabe – wir planen, fertigen vor und bauen. Mit dem Anspruch, den jedes hochwertige Gebäude verdient."
               it="Dalla prima idea alla consegna – pianifichiamo, prefabbrichiamo e costruiamo. Con la qualità che ogni edificio di pregio merita." />
          </p>
        </div>
      </section>

      {/* Leistungen Grid */}
      <section className="section-gap bg-white" data-testid="leistungen-grid">
        <div className="container-hb">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-px bg-hb-border">
            {leistungen.map((l, i) => (
              <Link
                key={l.id}
                to={`/leistungen/${l.slug}`}
                data-testid={`leistung-${l.id}`}
                className={`group bg-white card-hover flex flex-col overflow-hidden reveal reveal-delay-${(i % 4) + 1}`}
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={l.cardImage}
                    alt={l.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-8 flex-1 flex flex-col">
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h2 className="text-xl font-manrope font-bold text-hb-dark group-hover:text-hb-wood transition-colors">
                        {lang === "it" && l.it?.title ? l.it.title : l.title}
                      </h2>
                      <ArrowRight
                        size={18}
                        className="text-hb-muted group-hover:text-hb-wood group-hover:translate-x-1 transition-all flex-shrink-0 mt-1"
                      />
                    </div>
                    <p className="text-sm text-hb-text font-inter leading-relaxed mb-2">
                      {lang === "it" && l.it?.subtitle ? l.it.subtitle : l.subtitle}
                    </p>
                    <p className="text-sm text-hb-text font-inter leading-relaxed">
                      {lang === "it" && l.it?.description ? l.it.description : l.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-hb-dark" data-testid="leistungen-cta">
        <div className="container-hb text-center">
          <h2 className="text-3xl sm:text-4xl font-manrope font-extrabold text-white mb-4">
            <T de="Nicht sicher, was zu Ihrem Projekt passt?" it="Non siete sicuri di cosa si adatta al vostro progetto?" />
          </h2>
          <p className="text-base text-white/60 font-inter mb-8">
            <T de="Erzählen Sie uns davon – wir finden gemeinsam die richtige Lösung."
               it="Raccontatecelo – troveremo insieme la soluzione giusta." />
          </p>
          <Link to="/kontakt" data-testid="leistungen-cta-btn" className="btn-primary bg-white text-hb-dark hover:bg-hb-off-white">
            <T de="Kostenlos beraten lassen" it="Richiedere una consulenza gratuita" />
          </Link>
        </div>
      </section>
    </div>
  );
}
